"use client";

import { useSearch } from "@/context/SearchContext";
import { useLocalizedData } from "@/context/LocaleContext";
import { I18nData } from "@/types/i18n";
import { Branding } from "@/types/branding";
import { processHtmlLinks } from "@/utils/process-html-links";
import SearchInput from "./search-input";
import { SearchTipsModal } from "@/components/search/search-tips-modal";
import "./search.scss";
import React, { useRef, useState } from "react";
import { trackUserEvent } from "@/app/api/client-only";
import ClearIcon from "./images/clear.svg";
import Image from "next/image";

interface Props {
  i18nData?: I18nData;
  branding: Branding | null;
}

const Search = React.memo(function Search({ i18nData, branding }: Props) {
  const { searchResult, currentSearchTerm, filterState, clearAllFilters } =
    useSearch();
  const localizedData = useLocalizedData(i18nData);
  const [isTipsOpen, setIsTipsOpen] = useState(false);
  const searchTipsButtonRef = useRef<HTMLButtonElement>(null);

  const openInNewWindow = branding?.open_in_new_window !== false;
  const searchTitleHtml = processHtmlLinks(
    localizedData?.search_title,
    openInNewWindow,
  );

  const showSearchTips = branding?.search_tips === true;

  const searchTipsClick = () => {
    if (isTipsOpen) return;

    setIsTipsOpen(true);
    if (branding?._id) {
      trackUserEvent(branding._id, "c", "search_tips");
    }
  };

  // Collections is a special filter and does not count as an "active filter"
  // for the generic "Clear all filters" control.
  const hasActiveFilters = Object.keys(filterState.selected).some(
    (key) => key !== "collections",
  );

  const handleClearFilters = () => {
    clearAllFilters();
  };

  return (
    <div className="topLeftWrapper">
      <header
        id="header"
        dangerouslySetInnerHTML={{ __html: searchTitleHtml }}
      ></header>
      <SearchInput
        searchPlaceholder={localizedData?.search_placeholder ?? ""}
        searchButtonText={localizedData?.search_button_text}
        selectLibrariesPlaceholder={localizedData?.select_collections}
        clearButtonAriaLabel={localizedData?.clear_button_aria_label}
        branding={branding}
      />
      {showSearchTips && (
        <div className="searchTipsTriggerRow">
          <button
            ref={searchTipsButtonRef}
            type="button"
            className="searchTipsTrigger"
            aria-haspopup="dialog"
            disabled={isTipsOpen}
            onClick={searchTipsClick}
          >
            {localizedData?.search_tips_title_text}
          </button>
        </div>
      )}
      {showSearchTips && isTipsOpen && (
        <SearchTipsModal
          open={isTipsOpen}
          title={localizedData?.search_tips_title_text}
          content={localizedData?.search_tips_content}
          onClose={() => setIsTipsOpen(false)}
          returnFocusRef={searchTipsButtonRef}
        />
      )}
      <p id="resultsCount">
        {searchResult
          ? searchResult.total_count > 0
            ? localizedData?.results_found
                ?.replace("{{count}}", searchResult.total_count.toString())
                ?.replace("{{term}}", `"${currentSearchTerm}"`)
            : localizedData?.no_results_found?.replace(
                "{{term}}",
                `"${currentSearchTerm}"`,
              )
          : ""}
      </p>
      {hasActiveFilters && (
        <button onClick={handleClearFilters} className="clearFiltersButton">
          <Image src={ClearIcon} alt="" width={18} height={18} />
          <span>{localizedData?.clear_all_filters_button_text}</span>
        </button>
      )}
    </div>
  );
});

export default Search;
